import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Hero slogan element in index page.
   * @type {HTMLElement | null}
   */
  #heroSlogan = null;

  /**
   * Target progress for the hero morph.
   * @type {number}
   */
  #homeHeroTargetProgress = 0;

  /**
   * Smoothed progress applied to the UI.
   * @type {number}
   */
  #homeHeroRenderedProgress = 0;

  /**
   * RAF id for hero morph smoothing loop.
   * @type {number | null}
   */
  #homeHeroRafId = null;

  /**
   * Last RAF timestamp for frame-rate independent smoothing.
   * @type {number}
   */
  #homeHeroLastFrameTime = 0;

  #createFallbackHeroSlogan = () => {
    const sectionContent =
      document.querySelector("[id*='section_6PnLrM'] .group-block-content") ||
      document.querySelector('main .shopify-section:first-child .group-block-content');

    if (!(sectionContent instanceof HTMLElement)) return null;

    const slogan = document.createElement('div');
    slogan.className = 'text-block text-block--text_hero_slogan custom-typography';
    slogan.setAttribute('aria-hidden', 'true');
    const sloganText = Theme.translations?.home_hero_slogan || '¿Lista para descubrir la mejor versión de tus pestañas?';
    const paragraph = document.createElement('p');
    paragraph.textContent = sloganText;
    slogan.appendChild(paragraph);
    sectionContent.appendChild(slogan);
    return slogan;
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        // When in home-hero mode at scrollTop=0, the top:-1px trick makes
        // isIntersecting always false, but we need 'inactive' to show the
        // expanded hero logo. Only apply 'active' if we're actually scrolled.
        if (this.dataset.homeHero === 'true') {
          const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
          if (scrollTop <= 1) {
            this.dataset.stickyState = 'inactive';
            if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
            return;
          }
        }
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };


  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #handleWindowResize = () => {
    this.#updateHomeHeroProgress();
  };

  #smoothstep = (value) => {
    const t = Math.max(0, Math.min(1, value));
    return t * t * (3 - 2 * t);
  };

  #renderHomeHeroProgress = (timestamp = performance.now()) => {
    const previousTimestamp = this.#homeHeroLastFrameTime || timestamp;
    const deltaTime = Math.max(8, Math.min(48, timestamp - previousTimestamp));
    this.#homeHeroLastFrameTime = timestamp;

    const delta = this.#homeHeroTargetProgress - this.#homeHeroRenderedProgress;
    const interpolation = 1 - Math.pow(0.81, deltaTime / 16.67);
    this.#homeHeroRenderedProgress += delta * interpolation;

    if (Math.abs(delta) < 0.001) {
      this.#homeHeroRenderedProgress = this.#homeHeroTargetProgress;
    }

    const easedProgress = this.#smoothstep(this.#homeHeroRenderedProgress);
    this.style.setProperty('--home-hero-progress', easedProgress.toFixed(4));
    document.documentElement.style.setProperty('--home-hero-progress', easedProgress.toFixed(4));
    this.dataset.homeHeroState = this.#homeHeroRenderedProgress >= 0.995 ? 'collapsed' : 'expanded';

    if (!this.#heroSlogan || !this.#heroSlogan.isConnected) {
      this.#heroSlogan = document.querySelector('.text-block--text_hero_slogan');
      if (!(this.#heroSlogan instanceof HTMLElement)) {
        this.#heroSlogan = this.#createFallbackHeroSlogan();
      }
    }

    if (this.#heroSlogan instanceof HTMLElement) {
      const fadeProgress = Math.min(1, easedProgress * 1.95);
      const opacity = Math.max(0, 1 - fadeProgress);
      const translateY = fadeProgress * 22;

      this.#heroSlogan.style.opacity = opacity.toFixed(3);
      this.#heroSlogan.style.transform = `translate3d(0, ${translateY.toFixed(1)}px, 0)`;
      this.#heroSlogan.style.visibility = opacity < 0.02 ? 'hidden' : 'visible';
    }

    if (Math.abs(this.#homeHeroTargetProgress - this.#homeHeroRenderedProgress) > 0.001) {
      this.#homeHeroRafId = requestAnimationFrame(this.#renderHomeHeroProgress);
    } else {
      this.#homeHeroRafId = null;
      this.#homeHeroLastFrameTime = 0;
    }
  };

  #updateHomeHeroProgress = () => {
    if (this.dataset.homeHero !== 'true') return;

    let scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
    if (document.body.style.position === 'fixed' && document.body.style.top) {
      const lockedScrollTop = Math.abs(parseFloat(document.body.style.top));
      if (!Number.isNaN(lockedScrollTop)) scrollTop = lockedScrollTop;
    }
    const viewportHeight = Math.max(window.innerHeight || 0, 1);
    const heroSection = document.querySelector("[id*='section_6PnLrM'] .section");
    const heroHeight = heroSection instanceof HTMLElement ? heroSection.offsetHeight : viewportHeight;
    const threshold = Math.max(220, Math.min(heroHeight * 0.5, 620));
    this.#homeHeroTargetProgress = Math.max(0, Math.min(1, scrollTop / threshold));

    if (scrollTop <= 1) {
      this.#homeHeroTargetProgress = 0;
      this.#homeHeroRenderedProgress = 0;
      this.#homeHeroLastFrameTime = 0;

      // The IntersectionObserver with top:-1px always fires isIntersecting=false,
      // making data-sticky-state='active' even at scrollTop=0. This causes the
      // CSS to apply the solid white background and collapse the hero logo.
      // Force 'inactive' at the top so the transparent hero state is applied correctly.
      if (this.dataset.stickyState !== 'inactive') {
        this.dataset.stickyState = 'inactive';
      }
    } else if (this.getAttribute('sticky') === 'always' && this.dataset.stickyState === 'inactive') {
      // Restore 'active' sticky state when user has scrolled away from the very top.
      this.dataset.stickyState = 'active';
    }

    if (this.#homeHeroRafId === null) {
      this.#homeHeroRafId = requestAnimationFrame(this.#renderHomeHeroProgress);
    }
  };


  #updateScrollState = () => {
    this.#updateHomeHeroProgress();

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }

    if (this.dataset.homeHero === 'true' && stickyMode !== 'scroll-up' && stickyMode !== 'always') {
      document.addEventListener('scroll', this.#handleWindowScroll);
    }

    this.#updateHomeHeroProgress();
    window.addEventListener('resize', this.#handleWindowResize, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#handleWindowResize);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#homeHeroRafId !== null) {
      cancelAnimationFrame(this.#homeHeroRafId);
      this.#homeHeroRafId = null;
    }
    this.#homeHeroLastFrameTime = 0;
    this.style.removeProperty('--home-hero-progress');
    document.documentElement.style.removeProperty('--home-hero-progress');
    if (this.#heroSlogan instanceof HTMLElement) {
      this.#heroSlogan.style.removeProperty('opacity');
      this.#heroSlogan.style.removeProperty('transform');
      this.#heroSlogan.style.removeProperty('visibility');
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
